// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {find, get, includes, some} from 'lodash';

// Containers
import withAuth from './with-auth';

const accessTypes = ['admin', 'create', 'delete', 'read', 'update', 'write'];

const withPermissions = WrappedComponent => {
    const WithPermissions = props => {
        const getWrappedProps = () => ({
            ...props,
            hasAccessByRole,
            hasAccessByService,
            hasAccessByType,
            hasLockingAccess,
            isAuthorized,
        });

        const hasAccessByRole = ({allowRoles, adminOnly, requiresServiceAccess} = {}) => {
            const {
                allowRoles: propAllowRoles,
                adminOnly: propAdminOnly,
                requiresServiceAccess: propRequiresServiceAccess,
                scope,
            } = props;
            const currentAdminOnly = adminOnly || propAdminOnly;
            const currentRequiresServiceAccess = requiresServiceAccess || propRequiresServiceAccess;
            const currentAllowRoles = allowRoles || propAllowRoles;

            return (
                !currentAdminOnly &&
                !currentRequiresServiceAccess &&
                !!currentAllowRoles &&
                some(currentAllowRoles, role => includes(scope, role.toLowerCase()))
            );
        };

        const hasAccessByService = ({accessRequirement, adminOnly, requiresServiceAccess, serviceName} = {}) => {
            const {
                accessRequirement: propAccessRequirement,
                adminOnly: propAdminOnly,
                elementService,
                mediaItemService,
                requiresServiceAccess: propRequiresServiceAccess,
                scope,
            } = props;
            const serviceType = (serviceName || elementService || mediaItemService).toLowerCase();
            const currentAdminOnly = adminOnly || propAdminOnly;
            const currentRequiresServiceAccess = requiresServiceAccess || propRequiresServiceAccess;

            return (
                !currentAdminOnly &&
                currentRequiresServiceAccess &&
                (includes(scope, `${serviceType}_${accessRequirement || propAccessRequirement}`) ||
                    includes(scope, `${serviceType}_write`) ||
                    includes(scope, `${serviceType}_admin`))
            );
        };

        const hasAccessByType = ({accessRequirement, adminOnly, allowRoles, requiresServiceAccess} = {}) => {
            const {
                accessRequirement: propAccessRequirement,
                adminOnly: propAdminOnly,
                allowRoles: propAllowRoles,
                requiresServiceAccess: propRequiresServiceAccess,
                scope,
            } = props;

            return (
                !(adminOnly || propAdminOnly) &&
                !(requiresServiceAccess || propRequiresServiceAccess) &&
                !(allowRoles || propAllowRoles) &&
                some(
                    scope,
                    role => includes(role, accessRequirement || propAccessRequirement) || includes(role, 'admin')
                )
            );
        };

        const hasLockingAccess = recordIdArg => {
            const {lockAccess, lockedRecords, recordId, serviceKey, user} = props;
            const recordIdToCheck = recordIdArg || recordId;
            const lockedRecord = find(lockedRecords[serviceKey], ['recordId', recordIdToCheck]);

            return (!recordIdArg && !lockAccess) || !lockedRecord || lockedRecord.username === user;
        };

        const isAuthorized = () => {
            const {isAdmin} = props;

            return hasLockingAccess() && (isAdmin() || hasAccessByService() || hasAccessByType() || hasAccessByRole());
        };

        return <WrappedComponent {...getWrappedProps()} />;
    };

    WithPermissions.propTypes = {
        accessRequirement: PropTypes.oneOf(accessTypes),
        adminOnly: PropTypes.bool,
        allowRoles: PropTypes.array,
        elementService: PropTypes.string,
        isAdmin: PropTypes.func.isRequired,
        lockAccess: PropTypes.bool,
        lockedRecords: PropTypes.object,
        mediaItemGuid: PropTypes.string,
        mediaItemService: PropTypes.string.isRequired,
        notAuthorizedMessage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        recordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        requiresServiceAccess: PropTypes.bool,
        scope: PropTypes.array,
        serviceKey: PropTypes.string,
        user: PropTypes.string,
    };

    WithPermissions.defaultProps = {
        accessRequirement: 'read',
        scope: [],
    };

    WithPermissions.displayName = `WithPermissions(${getDisplayName(WrappedComponent)})`;

    const mapStateToProps = state => ({
        lockedRecords: get(state.recordLocking, 'lockedRecords', {}),
        mediaItemService: get(
            state.servicesSearchData,
            ['mediaItemDetail', 'searchData', 'content', 0, 'service', 'serviceName'],
            ''
        ).toLowerCase(),
        mediaItemGuid: get(state.router, 'routerParams.guid'),
    });

    return withAuth(connect(mapStateToProps)(WithPermissions));
};

function getDisplayName(WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

export default withPermissions;
